import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6LdwnIItAAAAAJzWPi00oUyc38jrJpMsgJTsY8lv";

const FreeTrialInfo: React.FC = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    facilityAddress: "",
    city: "",
    state: "",
    phoneNumber: "",
    imageKey: "",
    map: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [showCaptchaDialog, setShowCaptchaDialog] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Invalid email format";

    if (!form.facilityAddress.trim())
      newErrors.facilityAddress = "Facility address is required";
    if (!form.city.trim()) newErrors.city = "City is required";
    if (!form.state.trim()) newErrors.state = "State is required";
    if (!form.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required";

    return newErrors;
  };

  const submitFreeTrial = async (recaptchaToken: string) => {
    setLoading(true);
    setApiMessage(null);

    try {
      const response = await fetch(
        "https://apis.roomrounds.com/api/organization/saveOrganizationFreeTrial",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "text/plain",
          },
          body: JSON.stringify({
            organizationName: form.name,
            organizationEmail: form.email,
            location: form.facilityAddress,
            city: form.city,
            state: form.state,
            phoneNumber: form.phoneNumber,
            imageKey: form.imageKey,
            map: form.map,
            recaptchaToken,
          }),
        }
      );

      let result: {
        succeeded?: boolean;
        message?: string;
        httpStatusCode?: number;
      } | null = null;

      try {
        result = await response.json();
      } catch {
        result = null;
      }

      const succeeded =
        result?.succeeded === true &&
        (result.httpStatusCode == null || result.httpStatusCode < 400);

      if (response.ok && succeeded) {
        setSubmitted(true);
        setShowCaptchaDialog(false);
        setApiMessage(
          "Thank you! Go to mail box to get your free trial link."
        );
        setForm({
          name: "",
          email: "",
          facilityAddress: "",
          city: "",
          state: "",
          phoneNumber: "",
          imageKey: "",
          map: "",
        });
      } else {
        setSubmitted(false);
        setShowCaptchaDialog(false);
        setApiMessage(
          result?.message ||
            "Failed to submit free trial request. Please try again."
        );
        captchaRef.current?.reset();
      }
    } catch {
      setSubmitted(false);
      setShowCaptchaDialog(false);
      setApiMessage("An error occurred. Please try again later.");
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiMessage(null);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setShowCaptchaDialog(true);
  };

  const handleCaptchaChange = (token: string | null) => {
    if (!token) return;
    void submitFreeTrial(token);
  };

  const closeCaptchaDialog = () => {
    if (loading) return;
    setShowCaptchaDialog(false);
    captchaRef.current?.reset();
  };

  return (
    <section className="max-w-xl mx-auto py-12 px-4">
      <h2 className="text-3xl font-bold mb-6 text-center">
        Start Your Free Trial
      </h2>

      {!submitted && (
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6 space-y-4"
        >
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your  Name"
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <input
              type="text"
              name="name"
              placeholder="Your Organization Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              name="facilityAddress"
              placeholder="Address"
              value={form.facilityAddress}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
            {errors.facilityAddress && (
              <p className="text-red-600 text-sm mt-1">
                {errors.facilityAddress}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
              {errors.city && (
                <p className="text-red-600 text-sm mt-1">{errors.city}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2"
              />
              {errors.state && (
                <p className="text-red-600 text-sm mt-1">{errors.state}</p>
              )}
            </div>
          </div>

          <div>
            <input
              type="tel"
              name="phoneNumber"
              placeholder="Your Phone Number"
              value={form.phoneNumber}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 15),
                }))
              }
              className="w-full border rounded px-3 py-2"
            />
            {errors.phoneNumber && (
              <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>
            )}
          </div>

          <div>
            <textarea
              name="message"
              placeholder="Your  Message"
              className="w-full border rounded px-3 py-2"
              rows={4}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`bg-blue-600 text-white px-4 py-2 rounded w-full ${
              loading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
            }`}
          >
            {loading ? "Sending..." : "Request Free Trial"}
          </button>
        </form>
      )}

      {showCaptchaDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={closeCaptchaDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="captcha-dialog-title"
          >
            <button
              type="button"
              onClick={closeCaptchaDialog}
              disabled={loading}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 text-xl leading-none disabled:opacity-50"
              aria-label="Close"
            >
              ×
            </button>

            <h3
              id="captcha-dialog-title"
              className="text-xl font-semibold text-center mb-2"
            >
              Security Verification
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6">
              Please verify you are not a robot to continue.
            </p>

            <div className="flex justify-center min-h-[78px]">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>

            {loading && (
              <p className="text-sm text-blue-600 text-center mt-4">
                Submitting your request...
              </p>
            )}
          </div>
        </div>
      )}

      {apiMessage && (
        <div
          className={`relative p-4 pr-10 rounded text-center mt-4 ${
            submitted
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {apiMessage}
          <button
            type="button"
            onClick={() => setApiMessage(null)}
            className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded hover:bg-black/10 text-lg leading-none"
            aria-label="Close message"
          >
            ×
          </button>
        </div>
      )}

      <div className="mt-8 text-center text-gray-600">
        <div>
          Email:{" "}
          <a
            href="mailto:sales@Roomrounds.com"
            className="text-blue-600 hover:underline"
          >
            sales@Roomrounds.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default FreeTrialInfo;
